import type { Facility } from '../../utils/utils';
import {
    deriveFacilityStatus,
    getDaysRemaining,
    getFacilityAdoptionPercent,
    hasPendingSiteAdminInvitation,
    isFacilityDeactivated,
} from '../../utils/utils';
import type { FacilityStageKey } from './facilityTableTypes';

export function sortDeactivatedLast(facilities: Facility[]): Facility[] {
    return [...facilities].sort((a, b) => {
        const aDeactivated = isFacilityDeactivated(a);
        const bDeactivated = isFacilityDeactivated(b);
        if (aDeactivated !== bDeactivated) return aDeactivated ? 1 : -1;
        return 0;
    });
}

export function filterFacilitiesByStage(
    facilities: Facility[],
    stage: FacilityStageKey
): Facility[] {
    return facilities.filter((facility) => {
        const status = deriveFacilityStatus(facility);

        if (stage === 'onboarding') {
            return status === 'onboarding' || status === 'not-started';
        }

        if (stage === 'trial') {
            return status === 'trial' || status === 'trial-expired';
        }

        return status === 'active';
    });
}

export function applyStageFilter(
    facilities: Facility[],
    stage: FacilityStageKey,
    filterValue: string
): Facility[] {
    if (filterValue === 'all') return facilities;

    return facilities.filter((facility) => {
        const deactivated = isFacilityDeactivated(facility);

        if (filterValue === 'deactivated') return deactivated;
        if (deactivated) return false;

        const status = deriveFacilityStatus(facility);

        if (stage === 'onboarding') {
            const goLiveDays = getDaysRemaining(facility.start_date, 'go-live');
            if (filterValue === 'go-live-soon')
                return (
                    goLiveDays !== null && goLiveDays >= 0 && goLiveDays <= 7
                );
            if (filterValue === 'pending-site-admin-invitation')
                return hasPendingSiteAdminInvitation(facility);
            if (filterValue === 'scheduled') return status === 'onboarding';
            if (filterValue === 'not-started') return status === 'not-started';
            if (filterValue === 'overdue')
                return goLiveDays !== null && goLiveDays < 0;
        }

        if (stage === 'trial') {
            const trialDays = getDaysRemaining(
                facility.start_date,
                'trial',
                facility.end_date
            );
            if (filterValue === 'active-trial') return status === 'trial';
            const adoptionPercent = getFacilityAdoptionPercent(facility);
            if (filterValue === 'adoption-ready')
                return adoptionPercent >= 75 && adoptionPercent <= 100;
            if (filterValue === 'at-risk')
                return adoptionPercent >= 50 && adoptionPercent <= 74;
            if (filterValue === 'low-probability') return adoptionPercent < 50;
            if (filterValue === 'expiring') {
                return (
                    status === 'trial' && trialDays !== null && trialDays <= 7
                );
            }
            if (filterValue === 'expired') return status === 'trial-expired';
        }

        return true;
    });
}

export function sortFacilities(
    facilities: Facility[],
    stage: FacilityStageKey,
    sortValue: string
): Facility[] {
    const facilitiesCopy = [...facilities];

    facilitiesCopy.sort((left, right) => {
        const leftDeactivated = isFacilityDeactivated(left);
        const rightDeactivated = isFacilityDeactivated(right);
        if (leftDeactivated !== rightDeactivated) {
            return leftDeactivated ? 1 : -1;
        }

        if (sortValue === 'name') {
            return left.name.localeCompare(right.name);
        }

        if (sortValue === 'region') {
            return (left.region || '').localeCompare(right.region || '');
        }

        if (sortValue === 'licenses') {
            return right.licenses - left.licenses;
        }

        if (stage === 'onboarding') {
            const leftDays = getDaysRemaining(left.start_date, 'go-live');
            const rightDays = getDaysRemaining(right.start_date, 'go-live');

            if (sortValue === 'go-live') {
                return compareNullableNumbers(leftDays, rightDays);
            }
        }

        if (stage === 'trial') {
            const leftDays = getDaysRemaining(
                left.start_date,
                'trial',
                left.end_date
            );
            const rightDays = getDaysRemaining(
                right.start_date,
                'trial',
                right.end_date
            );

            if (sortValue === 'trial-ending') {
                return compareNullableNumbers(leftDays, rightDays);
            }

            if (sortValue === 'usage') {
                return getUsagePercent(right) - getUsagePercent(left);
            }
        }

        return left.name.localeCompare(right.name);
    });

    return facilitiesCopy;
}

function compareNullableNumbers(
    left: number | null,
    right: number | null
): number {
    if (left === null && right === null) return 0;
    if (left === null) return 1;
    if (right === null) return -1;
    return left - right;
}

function getUsagePercent(facility: Facility): number {
    if (facility.licenses <= 0) return 0;
    return facility.active_licenses / facility.licenses;
}
